import {
  addToast,
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
} from '@octano/global-ui';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Col, Row, Form } from 'reactstrap';
import { FormData, PeriodSelectorForm } from '../types';
import { downloadFromBlob } from '../../../utils/blob';
import { getBaseInfo, getTemplate } from '../api';
import useTexts from '../hooks/useTexts';

interface FilterListProps extends PeriodSelectorForm {}

export const FilterList = ({ periods }: Readonly<FilterListProps>) => {
  const texts = useTexts();
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      period: null,
    },
  });

  const optionsPeriods = useMemo<OutlinedSelectOptionType[]>(() => {
    return periods.map((p) => ({
      value: p.id,
      label: p.name,
    }));
  }, [periods]);

  const selectedPeriod = watch('period');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingBase, setIsLoadingBase] = useState<boolean>(false);

  const handleDownloadBaseInfo = async (data: FormData) => {
    setIsLoadingBase(true);
    if (!data.period) {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: texts.periodsSelector.form.downloadTemplateError,
      });
    }

    const res: any = await getBaseInfo({ periodId: Number(data.period.value) });
    if (res.error) {
      if (res.error.status === 404) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.periodsSelector.form.downloadBaseNotFound,
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.periodsSelector.form.downloadBaseError,
        });
      }
    } else {
      const blob = new Blob([res.data], { type: 'vnd.ms-excel' });
      const fileName = `${texts.periodsSelector.form.baseFileName}.xlsx`;
      downloadFromBlob(blob, fileName);
    }
    setIsLoadingBase(false);
  };

  const handleDownload = async () => {
    setIsLoading(true);
    if (!selectedPeriod) {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: texts.periodsSelector.form.downloadTemplateError,
      });
    }
    const res: any = await getTemplate({
      periodId: Number(selectedPeriod.value),
    });
    if (res.error) {
      if (res.error.status === 404) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.periodsSelector.form.downloadBaseNotFound,
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.periodsSelector.form.downloadBaseError,
        });
      }
    } else {
      const period = periods.find(
        (p) => p.id === Number(selectedPeriod?.value),
      );
      const fileName = `PlanillaDeInscripciónMasivaDeAsignaturas_${period?.code}.xlsx`;

      const blob = new Blob([res.data], { type: 'vnd.ms-excel' });
      downloadFromBlob(blob, fileName);
    }

    setIsLoading(false);
  };

  return (
    <Form
      onSubmit={handleSubmit(handleDownloadBaseInfo)}
      className="g-table-container mt-2 pb-4 pt-4"
    >
      <Row className="align-items-center">
        <Col md={6} lg={4} className="pl-0">
          <OutlinedSelect
            name="period"
            control={control}
            label={texts.periodsSelector.form.periodLabel}
            options={optionsPeriods}
          />
        </Col>
        <Col md={6} lg={8} className="d-flex justify-content-end mt-3">
          <Button
            text={texts.periodsSelector.form.downloadBase}
            type="submit"
            size="md"
            loading={isLoadingBase}
            disabled={isLoadingBase || !selectedPeriod || isSubmitting}
            className="mr-4"
          />
          <Button
            text={texts.periodsSelector.form.downloadTemplate}
            type="button"
            size="md"
            loading={isLoading}
            disabled={isSubmitting || !selectedPeriod || isLoadingBase}
            onClick={handleDownload}
          />
        </Col>
      </Row>
    </Form>
  );
};
